// lurik-frontend/src/presentation/pages/workspace/settings/security-section/components/CompanyTwoFactorToggle.tsx
import React from 'react';
import styled from 'styled-components';

interface CompanyTwoFactorToggleProps {
  enabled: boolean;
  onToggle: (enable: boolean) => void;
  loading: boolean;
}

// ✅ STYLED COMPONENTS (sem quebrar lógica existente)
const ToggleContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ToggleHeader = styled.div`
  margin-bottom: 20px;
`;

const ToggleTitle = styled.h3`
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const ToggleDescription = styled.p`
  color: #7f8c8d;
  font-size: 14px;
  margin: 0;
`;

const ToggleControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ToggleSwitch = styled.div`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + label {
    background-color: #3498db;
  }
  
  &:checked + label:before {
    transform: translateX(26px);
  }
  
  &:disabled + label {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleLabel = styled.label`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
  
  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const ToggleStatus = styled.div`
  display: flex;
  align-items: center;
`;

const StatusIndicator = styled.span<{ enabled: boolean }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.enabled ? '#d4edda' : '#f8d7da'};
  color: ${props => props.enabled ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.enabled ? '#c3e6cb' : '#f5c6cb'};
`;

const ToggleInfo = styled.div`
  margin-top: 20px;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li<{ enabled: boolean }>`
  color: ${props => props.enabled ? '#27ae60' : '#e74c3c'};
  font-size: 14px;
  margin: 8px 0;
  display: flex;
  align-items: center;
  
  &:before {
    content: "${props => props.enabled ? '✅' : '❌'}";
    margin-right: 8px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 14px;
  
  &:before {
    content: "⏳";
    margin-right: 8px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const CompanyTwoFactorToggle: React.FC<CompanyTwoFactorToggleProps> = ({
  enabled,
  onToggle,
  loading
}) => {
  // ✅ LÓGICA SIMPLES - sem quebrar nada existente
  const handleToggle = (checked: boolean) => {
    if (!loading) {
      onToggle(checked);
    }
  };

  return (
    <ToggleContainer>
      <ToggleHeader>
        <ToggleTitle>Autenticação de Dois Fatores (2FA)</ToggleTitle>
        <ToggleDescription>
          Habilite 2FA para todos os usuários da empresa
        </ToggleDescription>
      </ToggleHeader>

      <ToggleControls>
        <ToggleSwitch>
          <ToggleInput
            type="checkbox"
            id="company-2fa-toggle"
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={loading}
          />
          <ToggleLabel htmlFor="company-2fa-toggle" />
        </ToggleSwitch>

        <ToggleStatus>
          <StatusIndicator enabled={enabled}>
            {enabled ? 'Ativado' : 'Desativado'}
          </StatusIndicator>
        </ToggleStatus>
      </ToggleControls>

      <ToggleInfo>
        <InfoList>
          {enabled ? (
            <>
              <InfoItem enabled={true}>2FA está ativado para toda a empresa</InfoItem>
              <InfoItem enabled={true}>Todos os usuários devem configurar 2FA individual</InfoItem>
              <InfoItem enabled={true}>Login será obrigatório com código 2FA</InfoItem>
              <InfoItem enabled={true}>Maior segurança para dados da empresa</InfoItem>
            </>
          ) : (
            <>
              <InfoItem enabled={false}>2FA não está ativado para a empresa</InfoItem>
              <InfoItem enabled={false}>Login funciona apenas com senha</InfoItem>
              <InfoItem enabled={false}>Menor segurança para dados da empresa</InfoItem>
            </>
          )}
        </InfoList>
      </ToggleInfo>

      {loading && (
        <LoadingSpinner>
          Configurando...
        </LoadingSpinner>
      )}
    </ToggleContainer>
  );
};